import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { CPFeatureFlagGateway } from '../gateways/CPFeatureFlagGateway'
import type { FeatureFlagItem } from '../gateways/DTOs/FeatureFlagDTO'

const useFeatureFlagStore = defineStore('shared/feature-flag', () => {
  const gateway = CPFeatureFlagGateway.instance()
  const flags = ref<FeatureFlagItem[]>([])
  const fetched = ref(false)
  let generation = 0
  let pending: Promise<void> | null = null

  function isEnabled (name: string): boolean {
    return flags.value.find(f => f.name === name)?.enabled ?? false
  }

  function fetch (): Promise<void> {
    if (fetched.value) {
      return Promise.resolve()
    }

    if (pending) {
      return pending
    }

    const current = generation
    pending = load(current).finally(() => {
      if (generation === current) {
        pending = null
      }
    })

    return pending
  }

  async function load (current: number): Promise<void> {
    try {
      const result = await gateway.getAll()
      if (current !== generation) {
        return
      }
      if (!result.isError()) {
        flags.value = result.getValue() ?? []
      }
    } catch {
      if (current !== generation) {
        return
      }
      // Non-critical: feature flags unavailable
    }

    if (current === generation) {
      fetched.value = true
    }
  }

  function $reset (): void {
    generation += 1
    pending = null
    flags.value = []
    fetched.value = false
  }

  return { flags, fetched, isEnabled, fetch, $reset }
})

export default useFeatureFlagStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useFeatureFlagStore, (import.meta as any).hot))
}
